import {useNavigate, useParams} from 'react-router-dom';
import React from "react";
import {ApiUtility} from '../common/ApiUtility';
import {StringUtility} from "../common/StringUtility";
import {useModelFormEngine} from "../common/hooks/useModelFormEngine";
import {Constants} from "../common/Constants";
import {LeaveWithoutSavingWarningUtility} from "../common/LeaveWithoutSavingWarningUtility";
import {MessageDisplayerUtility} from "../common/MessageDisplayerUtility";
import {PillButton} from "../common/PillButton";
import {ConditionalRenderer} from "../common/ConditionalRenderer";
import {OverlayLoadingIndicator} from "../common/OverlayLoadingIndicator";

type ChangePasswordPageRouteParams = {
    username: string;
    token: string;
}

export let ChangePasswordPage = function () {
    const params = useParams<ChangePasswordPageRouteParams>()
    const navigate = useNavigate()
    const formEngine = useModelFormEngine(
        {
            label: 'Change Password',
            username: params.username,
            token: params.token,
            password: '',
            confirm_password: '',
            loading: false
        },
        undefined,
        fetchSupportingData,
        validateForm,
        undefined,
        save
    );

    function fetchSupportingData() {
        return ApiUtility.validatePasswordResetToken('' + params.token)
            .then(function (valid: boolean) {
                if (valid) {
                    return Promise.resolve({});
                } else {
                    MessageDisplayerUtility.error('Password reset request has expired or is invalid.')
                    navigate('/login')
                    return Promise.resolve({});
                }
            })
    }

    function validateForm(handleValidationResult: Function) {
        return new Promise(function (resolve, reject) {
            let validationErrors = [];
            let invalidFields = [];

            Promise.resolve({})
                .then(function () {
                    if (formEngine.dataStore.data().password && formEngine.dataStore.data().confirm_password) {
                        if (formEngine.dataStore.data().password.length < Constants.minPasswordLength) {
                            validationErrors.push(
                                `Passwords must be at least ${Constants.minPasswordLength} characters long.`
                            );
                            invalidFields.push('password');
                            invalidFields.push('confirm_password');
                        }

                        if (!StringUtility.containsUpperCaseLetter(formEngine.dataStore.data().password)) {
                            validationErrors.push('Passwords must contain at least one upper case letter.');
                            invalidFields.push('password');
                            invalidFields.push('confirm_password');
                        }

                        if (!StringUtility.containsNumber(formEngine.dataStore.data().password)) {
                            validationErrors.push('Passwords must contain at least one number.');
                            invalidFields.push('password');
                            invalidFields.push('confirm_password');
                        }

                        if (!StringUtility.containsSpecialCharacter(formEngine.dataStore.data().password)) {
                            validationErrors.push(
                                'Passwords must contain at least one of these characters: ' +
                                '!$%^&*()_+|~-=`{}[]:";\'<>?,./'
                            );
                            invalidFields.push('password');
                            invalidFields.push('confirm_password');
                        }

                        if (formEngine.dataStore.data().password !== formEngine.dataStore.data().confirm_password) {
                            validationErrors.push('Password does not match confirm password.');
                            invalidFields.push('password');
                            invalidFields.push('confirm_password');
                        }
                    } else {
                        validationErrors.push('Please fill out both password fields.');
                        invalidFields.push('password');
                        invalidFields.push('confirm_password');
                    }

                    if (!formEngine.dataStore.data().password) {
                        validationErrors.push('Please enter a password.');
                        invalidFields.push('password');
                        invalidFields.push('confirm_password');
                    }

                    return handleValidationResult(validationErrors, invalidFields, resolve);
                });
        });
    }

    function save(formModelData: object, disableNavigationBlocker: Function) {
        formEngine.dataStore.updateAndRender({loading: true})
        ApiUtility.changePassword(formModelData, '' + params.token)
            .then(() => {
                disableNavigationBlocker();
                LeaveWithoutSavingWarningUtility.disableLeaveWithoutSavingWarnings();
                MessageDisplayerUtility.success('Successfully updated password.');
                navigate('/login');
            })
            .catch((err: object) => {
                formEngine.dataStore.updateAndRender({loading: false})
                console.log("Error changing password: ", err);
                MessageDisplayerUtility.error('Error changing password.');
            });
    }

    function handleTextFieldChange(fieldName: string, event: React.ChangeEvent<HTMLInputElement>) {
        formEngine.dataStore.updateAndRender({[fieldName]: event.target.value});
    }

    return (
        <div className="pre-login-page">
            <ConditionalRenderer
                if={formEngine.dataStore.data().loading}
                renderer={() => <OverlayLoadingIndicator />}
            />
            <div className="pre-login-page-content">
                <div className="pre-login-page-header">
                    <img
                        src="/static/images/berger-logo-full-light-gray.svg"
                        className="pre-login-page-logo"
                    />
                </div>
                <form
                    className="common-form pre-login-form"
                    onSubmit={
                        (event: React.SyntheticEvent) => {
                            if (event) {
                                event.preventDefault();
                            }
                            formEngine.onClickSave()
                        }
                    }
                >
                    <div className="common-form-body">
                        <div className="pre-login-page-label">{formEngine.dataStore.data().label}</div>

                        <div className="pre-login-page-sub-label">
                            Password must be at least 8 characters long and contain an upper case letter,
                            number, and symbol.
                        </div>

                        <div className="common-form-body-row">
                            <div
                                className={formEngine.getFormFieldClasses(
                                    "common-form-field pre-login-field",
                                    "password"
                                )}
                            >
                                <div className="common-form-field-input-container">
                                    <input
                                        type="password"
                                        placeholder="New Password"
                                        className="common-form-input"
                                        onChange={(event) => handleTextFieldChange("password", event)}
                                        value={formEngine.dataStore.data().password}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="common-form-body-row">
                            <div
                                className={formEngine.getFormFieldClasses(
                                    "common-form-field pre-login-field",
                                    "confirm_password"
                                )}
                            >
                                <div className="common-form-field-input-container">
                                    <input
                                        type="password"
                                        placeholder="Confirm New Password"
                                        className="common-form-input"
                                        onChange={(event) => handleTextFieldChange("confirm_password", event)}
                                        value={formEngine.dataStore.data().confirm_password}
                                    />
                                </div>
                            </div>
                        </div>
                        <button className="enter-submit-support-button"></button>
                        <PillButton
                            buttonClasses="solid-green-button"
                            onClick={formEngine.onClickSave}
                            buttonText={formEngine.dataStore.data().label}
                        />
                    </div>
                </form>
                <div className="pre-login-page-footer">
                    <div className="pre-login-page-footer-label">
                        <div className="pre-login-page-footer-label-line" />
                        <div className="pre-login-page-footer-label-text">
                            Truck Dispatch
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
